import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

class Tickets extends Component {
  state = {
    tickets: [],
    categories: [],
    customers: [],
    admins: [],
    formData: {
      id: null,
      ticket_category_id: "",
      customer_id: "",
      admin_id: "",
      ticket_subject: "",
      ticket_description: "",
      ticket_create_datetime: "",
      ticket_solved_datetime: "",
      ticket_status: "Open",
      ticket_img: null,
    },
    viewData: null, // For View Modal
    imagePreview: "",
    loading: false,
    submitting: false,
    modalMode: "add",
    searchTerm: "",
    currentPage: 1,
    itemsPerPage: 10,
  };

  interval = null;

  componentDidMount() {
    this.fetchInitialData();
    this.interval = setInterval(this.fetchTickets, 30000);

    const modalEl = document.getElementById("ticketModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => this.resetForm());
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  fetchInitialData = () => {
    this.fetchCategories();
    this.fetchCustomers();
    this.fetchAdmins();
    this.fetchTickets();
  };

  fetchAdmins = async () => {
    try {
      const res = await axios.get("/admins");
      this.setState({ admins: res.data?.data || [] });
    } catch (err) { console.error(err); }
  };

  fetchCustomers = async () => {
    try {
      const res = await axios.get("/customers");
      this.setState({ customers: res.data?.data || [] });
    } catch (err) { console.error(err); }
  };

  fetchCategories = async () => {
    try {
      const res = await axios.get("/tickets/categories");
      this.setState({ categories: res.data?.data || [] });
    } catch (err) { console.error(err); }
  };

  fetchTickets = async () => {
    this.setState({ loading: this.state.tickets.length === 0 });
    try {
      const res = await axios.get("/tickets");
      this.setState({ tickets: res.data?.data || [] });
    } catch (err) {
      console.error("Failed to load tickets");
    } finally {
      this.setState({ loading: false });
    }
  };

  exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(this.state.tickets);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    XLSX.writeFile(wb, "Tickets_Report.xlsx");
  };

  exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Tickets Report", 14, 15);
    const tableColumn = ["ID", "Subject", "Customer", "Status", "Date"];
    const tableRows = this.state.tickets.map(t => [
      t.id, t.ticket_subject, t.customer_name, t.ticket_status, t.ticket_create_datetime
    ]);
    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
    doc.save("Tickets_Report.pdf");
  };

  confirmDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/tickets/${id}`);
          this.fetchTickets();
          Swal.fire("Deleted!", "Ticket has been removed.", "success");
        } catch {
          Swal.fire("Error", "Delete failed", "error");
        }
      }
    });
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        ticket_category_id: "",
        customer_id: "",
        admin_id: "",
        ticket_subject: "",
        ticket_description: "",
        ticket_create_datetime: "",
        ticket_solved_datetime: "",
        ticket_status: "Open",
        ticket_img: null,
      },
      imagePreview: "",
      submitting: false,
      modalMode: "add",
    });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState({
        formData: { ...this.state.formData, ticket_img: file },
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  openModal = (mode = "add", ticket = null) => {
    const baseUrl = axios.defaults.baseURL.replace("/api", "");
    if (mode === "view") {
      this.setState({ viewData: ticket }, () => {
        new window.bootstrap.Modal(document.getElementById("viewTicketModal")).show();
      });
      return;
    }

    if (mode === "edit" && ticket) {
      this.setState({
        modalMode: "edit",
        formData: { ...ticket, ticket_img: null },
        imagePreview: ticket.ticket_img ? `${baseUrl}/uploads/tickets/${ticket.ticket_img}` : "",
      });
    } else {
      this.resetForm();
    }
    new window.bootstrap.Modal(document.getElementById("ticketModal")).show();
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) data.append(key, formData[key]);
    });

    this.setState({ submitting: true });
    try {
      if (modalMode === "add") {
        await axios.post("/tickets", data);
      } else {
        await axios.post(`/tickets/${formData.id}?_method=PUT`, data);
      }
      this.fetchTickets();
      window.bootstrap.Modal.getInstance(document.getElementById("ticketModal")).hide();
      Swal.fire({ icon: "success", title: "Success", timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Error", "Could not save ticket", "error");
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { tickets, categories, customers, admins, formData, viewData, imagePreview, loading, submitting, modalMode, searchTerm, currentPage, itemsPerPage } = this.state;
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    const filteredTickets = tickets.filter(t => 
      t.ticket_subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3">
              <div className="row align-items-center g-3">
                <div className="col-md-4">
                  <h5 className="mb-0">Ticket Management</h5>
                </div>
                <div className="col-md-8 text-md-end">
                  <div className="d-flex flex-wrap justify-content-md-end gap-2">
                    <input 
                      type="text" 
                      className="form-control form-control-sm w-auto" 
                      placeholder="Search..." 
                      onChange={(e) => this.setState({ searchTerm: e.target.value, currentPage: 1 })}
                    />
                    <button className="btn btn-sm btn-outline-secondary" onClick={this.exportToExcel}>Excel</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={this.exportToPDF}>PDF</button>
                    <button className="btn btn-sm btn-primary" onClick={() => this.openModal("add")}>+ New Ticket</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">Ticket Detail</th>
                      <th>Customer</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th className="text-end pe-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="5" className="text-center py-5">Loading...</td></tr>
                    ) : currentItems.map((t) => (
                      <tr key={t.id}>
                        <td className="ps-3">
                           <div className="fw-bold text-dark">{t.ticket_subject}</div>
                           <small className="text-muted">No: {t.ticket_number || t.id}</small>
                        </td>
                        <td>{t.customer_name || "N/A"}</td>
                        <td><span className="badge bg-light text-dark border">{t.category_name}</span></td>
                        <td>
                          <span className={`badge ${t.ticket_status === 'Open' ? 'bg-info' : 'bg-success'}`}>
                            {t.ticket_status}
                          </span>
                        </td>
                        <td className="text-end pe-3">
                          <button className="btn btn-sm btn-light-info me-1" onClick={() => this.openModal("view", t)} title="View">
                              View
                          </button>
                          <button className="btn btn-sm btn-light-warning me-1" onClick={() => this.openModal("edit", t)} title="Edit">
                              Edit
                          </button>
                          <button className="btn btn-sm btn-light-danger" onClick={() => this.confirmDelete(t.id)} title="Delete">
                              Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card-footer bg-white d-flex justify-content-between align-items-center">
              <small className="text-muted">Showing {currentItems.length} of {filteredTickets.length}</small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => this.setState({ currentPage: currentPage - 1 })}>Prev</button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => this.setState({ currentPage: i + 1 })}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => this.setState({ currentPage: currentPage + 1 })}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        {/* --- VIEW MODAL --- */}
        <div className="modal fade" id="viewTicketModal" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ticket Details</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                {viewData && (
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="text-muted small d-block">Subject</label>
                      <p className="fw-bold">{viewData.ticket_subject}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted small d-block">Status</label>
                      <span className="badge bg-primary">{viewData.ticket_status}</span>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted small d-block">Category</label>
                      <p>{viewData.category_name}</p>
                    </div>
                    <div className="col-12 border-top pt-2">
                      <label className="text-muted small d-block">Description</label>
                      <p className="text-secondary">{viewData.ticket_description}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted small d-block">Created At</label>
                      <p className="small">{viewData.ticket_create_datetime}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted small d-block">Assigned To</label>
                      <p className="small">{viewData.fullname || "Unassigned"}</p>
                    </div>
                    {viewData.ticket_img && (
                      <div className="col-12 text-center border-top pt-2">
                        <label className="text-muted small d-block mb-2">Attachment</label>
                        <img src={`${baseUrl}/uploads/tickets/${viewData.ticket_img}`} className="img-fluid rounded shadow-sm" style={{maxHeight: '200px'}} alt="ticket" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- EDIT/ADD MODAL --- */}
        <div className="modal fade" id="ticketModal" tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">{modalMode === "add" ? "New Ticket" : "Edit Ticket"}</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div className="modal-body row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Customer</label>
                    <select name="customer_id" className="form-select" value={formData.customer_id} onChange={this.handleChange} required>
                      <option value="">Select Customer</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Category</label>
                    <select name="ticket_category_id" className="form-select" value={formData.ticket_category_id} onChange={this.handleChange} required>
                      <option value="">Select Category</option>
                      {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.category_name}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Subject</label>
                    <input type="text" name="ticket_subject" className="form-control" value={formData.ticket_subject} onChange={this.handleChange} required />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea name="ticket_description" className="form-control" rows="3" value={formData.ticket_description} onChange={this.handleChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Admin</label>
                    <select name="admin_id" className="form-select" value={formData.admin_id} onChange={this.handleChange}>
                      <option value="">Select Admin</option>
                      {admins.map((a) => <option key={a.id} value={a.id}>{a.fullname}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Status</label>
                    <select name="ticket_status" className="form-select" value={formData.ticket_status} onChange={this.handleChange}>
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Attachment</label>
                    <input type="file" className="form-control" onChange={this.handleFileChange} />
                    {imagePreview && <img src={imagePreview} className="mt-2 rounded" width="80" alt="preview" />}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Saving..." : "Save Ticket"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Tickets;